import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, StyleSheet, Switch, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { getDepartments } from '../../services/departmentService';
import {
  createDesignation,
  getDesignationDetails,
  updateDesignation,
} from '../../services/designationService';
import type { DesignationDetails } from '../../types/designation';
import { activeConfigurationFilters } from '../../utils/configurationFilters';
import {
  isConcurrencyConflict,
  organizationErrorMessage,
} from '../../utils/organizationErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type CreateProps = NativeStackScreenProps<
  RootStackParamList,
  'DesignationCreate'
>;
type EditProps = NativeStackScreenProps<RootStackParamList, 'DesignationEdit'>;
type Props = CreateProps | EditProps;

type FormState = {
  designationCode: string;
  designationName: string;
  description: string;
  departmentId: number | null;
  departmentLabel: string | null;
  designationLevel: string;
  isManagerial: boolean;
  isActive: boolean;
};

const EMPTY: FormState = {
  designationCode: '',
  designationName: '',
  description: '',
  departmentId: null,
  departmentLabel: null,
  designationLevel: '1',
  isManagerial: false,
  isActive: true,
};

function fromDetails(item: DesignationDetails): FormState {
  return {
    designationCode: item.designationCode,
    designationName: item.designationName,
    description: item.description ?? '',
    departmentId: item.departmentId,
    departmentLabel: item.departmentName,
    designationLevel: String(item.designationLevel),
    isManagerial: item.isManagerial,
    isActive: item.isActive !== false,
  };
}

export function validateDesignationForm(
  form: FormState,
  isEdit: boolean,
): string | null {
  if (!isEdit && !form.designationCode.trim()) {
    return 'Designation code is required.';
  }
  if (!form.designationName.trim()) return 'Designation name is required.';
  const level = Number(form.designationLevel);
  if (!Number.isInteger(level) || level <= 0) {
    return 'Designation level must be a positive integer.';
  }
  return null;
}

function isEditRoute(props: Props): props is EditProps {
  return props.route.name === 'DesignationEdit';
}

export function DesignationFormScreen(props: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      readOnlyLabel: {
        ...typography.caption,
        color: palette.textSecondary,
        fontWeight: '600' as const,
      },
      readOnlyValue: { ...typography.body, color: palette.textPrimary },
      error: { ...typography.caption, color: palette.error },
      switchLabel: { ...typography.body, color: palette.textPrimary },
    }),
    [palette],
  );

  const isEdit = isEditRoute(props);
  const designationId = isEdit ? props.route.params.designationId : null;
  const navigation = props.navigation;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [details, setDetails] = useState<DesignationDetails | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const dirty = useRef(false);
  const savingLock = useRef(false);

  const load = useCallback(async () => {
    if (!designationId) return;
    setLoading(true);
    try {
      const data = await getDesignationDetails(designationId);
      setDetails(data);
      setForm(fromDetails(data));
      dirty.current = false;
      setError(null);
    } catch (caught) {
      setError(
        organizationErrorMessage(
          caught,
          'The requested record could not be loaded.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [designationId]);

  useEffect(() => {
    if (isEdit) load().catch(() => undefined);
  }, [isEdit, load]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    dirty.current = true;
    setForm(current => ({ ...current, [key]: value }));
  }

  function confirmLeave() {
    if (!dirty.current) {
      navigation.goBack();
      return;
    }
    Alert.alert(
      'Discard changes?',
      'You have unsaved changes. Leave without saving?',
      [
        { text: 'Stay', style: 'cancel' },
        {
          text: 'Discard',
          style: 'destructive',
          onPress: () => navigation.goBack(),
        },
      ],
    );
  }

  const loadDepartmentOptions = useCallback(
    async (search: string): Promise<SelectorOption[]> => {
      const result = await getDepartments({
        page: 1,
        pageSize: 20,
        filters: activeConfigurationFilters({ search: search || undefined }),
      });
      return result.items.map(item => ({
        id: item.departmentId,
        label: item.departmentName,
        subtitle: item.departmentCode,
      }));
    },
    [],
  );

  async function submit() {
    if (savingLock.current) return;
    const validation = validateDesignationForm(form, isEdit);
    if (validation) {
      setFieldError(validation);
      return;
    }

    savingLock.current = true;
    setSaving(true);
    setFieldError(null);
    setError(null);

    const designationLevel = Number(form.designationLevel);

    try {
      if (isEdit && designationId && details) {
        await runOperation('update', () => updateDesignation(designationId, {
          departmentId: form.departmentId,
          designationName: form.designationName,
          description: form.description,
          designationLevel,
          displayOrder: details.displayOrder ?? 0,
          isManagerial: form.isManagerial,
          isActive: form.isActive,
          rowVersion: details.rowVersion,
        }));
      } else {
        await runOperation('add', () => createDesignation({
          departmentId: form.departmentId,
          designationCode: form.designationCode,
          designationName: form.designationName,
          description: form.description,
          designationLevel,
          isManagerial: form.isManagerial,
        }));
      }
      dirty.current = false;
      navigation.goBack();
    } catch (caught) {
      if (isConcurrencyConflict(caught) && isEdit) {
        const message = organizationErrorMessage(caught);
        await load();
        setError(message);
      } else {
        setError(
          organizationErrorMessage(
            caught,
            isEdit
              ? 'Unable to update designation.'
              : 'Unable to create designation.',
          ),
        );
      }
    } finally {
      savingLock.current = false;
      setSaving(false);
    }
  }

  const permission = isEdit
    ? PERMISSIONS.DESIGNATION_UPDATE
    : PERMISSIONS.DESIGNATION_CREATE;

  return (
    <PermissionGuard permission={permission} fallback={<UnauthorizedScreen />}>
      {loading ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title={isEdit ? 'Edit designation' : 'Create designation'}
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading designation" />
        </AppScreen>
      ) : error && isEdit && !details ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title="Edit designation"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : (
        <AppScreen webLayout="narrow" scroll keyboardSafe>
          <ScreenHeader
            title={isEdit ? 'Edit designation' : 'Create designation'}
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
          <AppCard style={styles.card}>
            {isEdit ? (
              <>
                <Text style={themed.readOnlyLabel}>Designation code</Text>
                <Text style={themed.readOnlyValue}>{form.designationCode}</Text>
              </>
            ) : (
              <AppInput
                label="Designation code *"
                value={form.designationCode}
                onChangeText={value => setField('designationCode', value)}
                autoCapitalize="characters"
              />
            )}
            <AppInput
              label="Designation name *"
              value={form.designationName}
              onChangeText={value => setField('designationName', value)}
            />
            <AppInput
              label="Description"
              value={form.description}
              onChangeText={value => setField('description', value)}
            />
            <SearchableEntitySelector
              label="Department"
              selectedValue={form.departmentId}
              selectedLabel={form.departmentLabel}
              loadOptions={loadDepartmentOptions}
              onSelect={option => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  departmentId: option.id,
                  departmentLabel: option.label,
                }));
              }}
              onClear={() => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  departmentId: null,
                  departmentLabel: null,
                }));
              }}
              placeholder="Search departments"
            />
            <AppInput
              label="Designation level *"
              value={form.designationLevel}
              onChangeText={value => setField('designationLevel', value)}
              keyboardType="number-pad"
            />
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>Managerial</Text>
              <Switch
                value={form.isManagerial}
                onValueChange={value => setField('isManagerial', value)}
              />
            </View>
            {isEdit ? (
              <View style={styles.switchRow}>
                <Text style={themed.switchLabel}>Active</Text>
                <Switch
                  value={form.isActive}
                  onValueChange={value => setField('isActive', value)}
                />
              </View>
            ) : null}
            {fieldError ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {fieldError}
              </Text>
            ) : null}
            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}
            <AppButton title="Save" onPress={submit} loading={saving} />
            <AppButton
              title="Cancel"
              variant="secondary"
              onPress={confirmLeave}
            />
          </AppCard>
          </View>
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.lg },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});
